import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ColorLibraryIcon from "../../../../assets/icons/ColorLibrary";
import SegmentPreview from "../../../../assets/icons/SegmentPreview";
import WheelPreview from "../../../../assets/icons/WheelPreview";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import {
  setColorPaletteDefault,
  setSelectedPaletteTab,
} from "../../../../redux/reducers/colorPalette";
import { openModal } from "../../../../redux/reducers/dialog";
import {
  pushToHistoryAction,
  setWheelColorPalette,
  updateSegmentColorPalette,
} from "../../../../redux/reducers/segmentBuilder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getPaletteColors } from "../../../../redux/selectors/colorPalette";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { savePalette } from "../../../../services/api";
import { ASSETS } from "../../../../utils/config";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import CustomTabs from "../CustomTabs";
import ColorLibrary from "./ColorLibrary";
import { PALETTE_TYPES } from "./constants";
import Image from "./Image";
import Prompt from "./Prompt";
import SegmentColor from "./SegmentColor";
import * as Styled from "./styles";

// API KEYS
// usage type 1 for wheel, 2 for segment
// tab type 1 for prompt, 2 for image, 3 for color
// type 1 for save, 2 for recent


export default function ColorPaletteModal({
  onClose,
  paletteType = PALETTE_TYPES.SEGMENT,
  isWheelWizard = false,
  onPaletteChange,
}) {
  const [showLibrary, setShowLibrary] = useState(false);
  const wheelId = useSelector(getWheelId);
  const dispatch = useDispatch();
  const selectedTab = useSelector(
    (state) => state.colorPalette.selectedPaletteTab
  );
  const selectedArc = useSelector(getSelectedSegment) || {};
  const { settings: segmentSettings = {} } = selectedArc;
  const { color: initialSegmentColor } = segmentSettings;
  const { handleChange } = useSegmentSettings();
  const [searchText, setSearchText] = useState("");
  const { updateSegmentColors } = useSegmentPalettes();

  const types = ["prompt", "image", "color"];

  const paletteColors = useSelector((state) =>
    getPaletteColors(state, types[selectedTab], paletteType)
  );

  const selectedPalette = paletteColors.find((item) => item.selected);

  useEffect(() => {
    if (onPaletteChange && selectedPalette) {
      onPaletteChange(selectedPalette);
    }
  }, [selectedPalette, onPaletteChange]);

  const handleTabChange = (e, newVal) => {
    dispatch(setSelectedPaletteTab(newVal));
  };

  const handleConfirm = () => {
    if (isWheelWizard) {
      handleSelectPalette();
      return;
    }
    if (
      paletteType == PALETTE_TYPES.SEGMENT &&
      selectedPalette.colors[0]?.hex !== initialSegmentColor
    )
      dispatch(
        openModal({
          onConfirm: handleSelectPalette,
          title: "Change Segment Color",
          subtitle:
            "This Palette will change the Segment's Color. Proceed anyway?",
        })
      );
    else if (paletteType == PALETTE_TYPES.WHEEL && !isWheelWizard)
      dispatch(
        openModal({
          onConfirm: handleSelectPalette,
          title: "Change Wheel Palette",
          subtitle:
            "Changing the Wheel Palette will update the color palettes for each Segment as well. This can take several seconds, but will occur in the background as you continue to build. Still Proceed?",
        })
      );
    else handleSelectPalette();
  };

  const handleSelectPalette = async () => {
    const TAB_ENUMS = {
      0: 1, // Prompt
      1: 3, // Image
      2: 2, // Color
    };
    try {
      // Call the savePalette API with the selected palette
      const response = await savePalette({
        id: typeof selectedPalette.id === 'string' ? undefined : selectedPalette.id,
        colors: selectedPalette.colors,
        palette_name: selectedPalette.paletteName,
        wheel_id: wheelId,
        type: 2,
        usage_type: paletteType == PALETTE_TYPES.SEGMENT ? 2 : 1,
        tab_type: TAB_ENUMS[selectedTab],
        metadata: {
          prompt: selectedTab == 0 ? selectedPalette.prompt : undefined,
          image: selectedTab == 1 ? selectedPalette.imageUrl : undefined,
          color: selectedTab == 2 ? selectedPalette.colors[0]?.hex : undefined,
        },
      });

      if (response.data.success) {
        const paletteId = response.data.data.id;
        dispatch(pushToHistoryAction());

        if (paletteType == PALETTE_TYPES.SEGMENT) {
          handleChange("segmentColor", null, selectedPalette.colors[0]?.hex);

          dispatch(
            updateSegmentColorPalette({
              palette: selectedPalette,
              paletteId,
              id: selectedArc?.id,
            })
          );
        } else {
          dispatch(
            setWheelColorPalette({
              paletteData: selectedPalette,
              paletteId,
            })
          );
          toast.success(
            isWheelWizard
              ? "Palette created for your wheel."
              : "Wheel palette updated successfully"
          );

          updateSegmentColors(selectedPalette);
        }

        // Reset the palette types in the reducer
        dispatch(setColorPaletteDefault({ paletteType }));
        onClose();
      } else {
        // Optionally handle API failure
        console.error("Failed to save palette. Please try again.");
      }
    } catch (error) {
      console.error("Error saving palette:", error);
    }
  };

  const tabOptions = new Map([
    [
      "0",
      {
        label: "Prompt",
        component: (
          <Prompt
            paletteType={paletteType}
            handleSelect={handleConfirm}
            showSelectButton={isWheelWizard && !!selectedPalette}
          />
        ),
      },
    ],
    [
      "1",
      {
        label: "Image",
        component: (
          <Image
            paletteType={paletteType}
            handleSelect={handleConfirm}
            showSelectButton={isWheelWizard && !!selectedPalette}
          />
        ),
      },
    ],
    // [
    //   "2",
    //   {
    //     label:
    //       paletteType == PALETTE_TYPES.WHEEL ? "Base Color" : "Segment Color",
    //     component: (
    //       <SegmentColor
    //         paletteType={paletteType}
    //         handleSelect={handleConfirm}
    //         showSelectButton={isWheelWizard && !!selectedPalette}
    //       />
    //     ),
    //   },
    // ],
  ]);

  return (
    <Styled.ModalContainer
      style={
        !isWheelWizard
          ? {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "75vh",
            }
          : null
      }
    >
      {isWheelWizard && (
        <Styled.LibraryButton
          onClick={() => setShowLibrary(!showLibrary)}
          style={{ position: "absolute", top: 10, right: 10 }}
        >
          {showLibrary ? <ArrowBackIcon /> : <ColorLibraryIcon />}{" "}
          <TextView>{showLibrary ? "Go Back" : "Library"}</TextView>
        </Styled.LibraryButton>
      )}
      {!isWheelWizard && (
        <>
          {" "}
          <Styled.ModalHeader>
            <div>
              {showLibrary ? (
                <Styled.Row>
                  <IconButton onClick={() => setShowLibrary(false)}>
                    <ArrowBackIcon style={{ color: "#000" }} />
                  </IconButton>
                  <div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "28px 1fr",
                        alignItems: "center",
                      }}
                    >
                      <ColorLibraryIcon color="black" />
                      <TextView variant="title">Color Library</TextView>
                    </div>
                    <TextView>
                      Store and organize color palettes for future use!
                    </TextView>
                  </div>
                </Styled.Row>
              ) : (
                <>
                  <TextView variant="title">Palette Generator</TextView>
                  <TextView>
                    Generate unlimited color palettes for your {paletteType}.
                  </TextView>
                </>
              )}
            </div>
            {showLibrary ? (
              <Input
                variant={"secondary"}
                isSearch={true}
                containerStyle={{ marginBottom: 0 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            ) : (
              <Styled.Row>
                <Styled.LibraryButton onClick={() => setShowLibrary(true)}>
                  <ColorLibraryIcon />
                  <TextView>{"Library"}</TextView>
                </Styled.LibraryButton>

                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Styled.Row>
            )}
          </Styled.ModalHeader>
        </>
      )}

      {showLibrary ? (
        <ColorLibrary
          onClose={onClose}
          onBack={() => setShowLibrary(false)}
          searchText={searchText}
          setSearchText={setSearchText}
          paletteType={paletteType}
          isWheelWizard={isWheelWizard}
          onPaletteChange={onPaletteChange}
        />
      ) : (
        <>
          <Styled.ModalContent>
            <Styled.Grid>
              <Styled.PreviewContainer>
                <TextView variant="subtitle" style={{ fontWeight: 700 }}>
                  Preview
                </TextView>
                <TextView>Generated color palette will preview here!</TextView>
                <Styled.ImageWrapper
                  style={
                    paletteType == PALETTE_TYPES.WHEEL ? { padding: 20 } : {}
                  }
                >
                  {paletteType == PALETTE_TYPES.SEGMENT ? (
                    selectedPalette?.colors.length ? (
                      <SegmentPreview colors={selectedPalette?.colors} />
                    ) : (
                      <img
                        src={ASSETS.SegmentPreview}
                        style={{ width: "100%", margin: "12px 0px" }}
                      />
                    )
                  ) : (
                    <WheelPreview colors={selectedPalette?.colors} />
                  )}
                </Styled.ImageWrapper>
              </Styled.PreviewContainer>
              <Styled.StylingContainer>
                <CustomTabs
                  selectedTab={selectedTab}
                  handleTabChange={handleTabChange}
                  options={tabOptions}
                />
              </Styled.StylingContainer>
            </Styled.Grid>
          </Styled.ModalContent>
          {!isWheelWizard && (
            <Styled.ModalFooter>
              <Styled.ButtonsContainer>
                <Button variant={"secondary"} onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm}>Select</Button>
              </Styled.ButtonsContainer>
            </Styled.ModalFooter>
          )}
        </>
      )}
    </Styled.ModalContainer>
  );
}
